import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InfoboxComponent } from '@recruitee/components/infobox';
import { TranslatePipe } from '@recruitee/i18n';
import type { DialogComponentI, SelectOption } from '@recruitee/user-interface';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormFieldComponent,
  LabelComponent,
  NewItemButtonComponent,
  SelectComponent,
} from '@recruitee/user-interface';

type DialogData = {
  jobCategories: string[];
  jobsCount: number;
};

export type ManageEeoDialogResult =
  | {
      action: 'enable' | 'disable';
      jobCategory?: string;
    }
  | undefined;

enum Step {
  Action = 1,
  Category = 2,
}

@Component({
  selector: 'rt-manage-eeo-dialog',
  templateUrl: './manage-eeo-dialog.component.html',
  styleUrls: ['./manage-eeo-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    NgTemplateOutlet,
    DialogFooterComponent,
    ButtonComponent,
    NewItemButtonComponent,
    FormFieldComponent,
    LabelComponent,
    SelectComponent,
    TranslatePipe,
    InfoboxComponent,
  ],
})
export class ManageEeoDialogComponent
  implements DialogComponentI<DialogData, ManageEeoDialogResult>
{
  @ViewChild('stepAction', { static: true }) public stepAction: TemplateRef<void>;
  @ViewChild('stepCategory', { static: true }) public stepCategory: TemplateRef<void>;

  public jobsCount = 0;
  public categoryOptions: SelectOption[] = [];

  public currentStep: Step = Step.Action;
  public categoryControl = new FormControl('', [Validators.required]);

  private ngOnDestroy$ = new Subject<void>();

  public get hasFooter(): boolean {
    return this.currentStep === Step.Category;
  }

  constructor(
    private dialogRef: DialogRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.categoryControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getBodyForCurrentStep(): TemplateRef<any> | null {
    switch (this.currentStep) {
      case Step.Action:
        return this.stepAction;
      case Step.Category:
        return this.stepCategory;
      default:
        return null;
    }
  }

  public setDialogData(data: DialogData, triggerUpdate: boolean): void {
    const { jobCategories, jobsCount } = data;
    this.categoryOptions = jobCategories.map(category => ({ label: category, value: category }));
    this.jobsCount = jobsCount;
  }

  public enable() {
    this.currentStep = Step.Category;
    this.cdRef.detectChanges();
  }

  public disable() {
    this.dialogRef.close({
      action: 'disable',
    });
  }

  public save() {
    this.dialogRef.close({
      action: 'enable',
      jobCategory: this.categoryControl.value,
    });
  }

  public cancel() {
    this.dialogRef.close();
  }
}
